import { Controller } from "@hotwired/stimulus"

/**
 * Handles dialogue block editing functionality including:
 * - Adding/removing speakers
 * - Toggling character/narrator type fields
 * - Uploading speaker avatar images
 * - Updating speaker options in dialogue lines
 */
export default class extends Controller {
  static targets = [
    "speakersList",
    "speaker",
    "linesList",
    "line",
    "lineSpeaker",
    "lineText",
    "newLineSpeaker"
  ]

  connect() {
    // Initialize any dynamic targets
    this.speakerCount = this.speakerTargets.length
  }

  /**
   * Toggle visibility of character vs narrator fields when speaker type changes
   */
  speakerTypeChanged(event) {
    const speakerIndex = event.target.dataset.speakerIndex
    const speakerType = event.target.value
    const speakerElement = this.speakerTargets[speakerIndex]

    if (!speakerElement) return

    const characterFields = speakerElement.querySelector(".character-fields")
    const narratorFields = speakerElement.querySelector(".narrator-fields")

    if (speakerType === "narrator") {
      characterFields?.classList.add("hidden")
      narratorFields?.classList.remove("hidden")
    } else {
      characterFields?.classList.remove("hidden")
      narratorFields?.classList.add("hidden")
    }
  }

  /**
   * Upload a speaker avatar image
   */
  async uploadImage(event) {
    const file = event.target.files[0]
    if (!file) return

    const uploadUrl = event.target.dataset.uploadUrl
    const speakerIndex = event.target.dataset.speakerIndex

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"]
    if (!allowedTypes.includes(file.type)) {
      alert("Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.")
      event.target.value = ""
      return
    }

    // Show loading state
    const speakerElement = this.speakerTargets[speakerIndex]
    const previewContainer = speakerElement?.querySelector(".flex.gap-2.items-center")
    if (previewContainer) {
      previewContainer.classList.add("opacity-50")
    }

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      const formData = new FormData()
      formData.append("file", file)

      const response = await fetch(uploadUrl, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": csrfToken,
          Accept: "application/json"
        },
        body: formData
      })

      if (response.ok) {
        const data = await response.json()
        // Reload to show updated image
        setTimeout(() => {
          window.location.reload()
        }, 300)
      } else {
        const data = await response.json().catch(() => ({}))
        alert(`Upload failed: ${data.error || "Unknown error"}`)
        if (previewContainer) {
          previewContainer.classList.remove("opacity-50")
        }
      }
    } catch (error) {
      console.error("Image upload failed:", error)
      alert("Image upload failed. Please try again.")
      if (previewContainer) {
        previewContainer.classList.remove("opacity-50")
      }
    }

    // Reset file input
    event.target.value = ""
  }

  /**
   * Add a new speaker to the dialogue block
   * Note: This submits the form to add the speaker server-side
   */
  addSpeaker(event) {
    event.preventDefault()

    // Get the form and add a hidden flag to indicate adding a speaker
    const form = this.element.querySelector("form")
    if (!form) return

    // Create a hidden input to signal we want to add a speaker
    const addSpeakerInput = document.createElement("input")
    addSpeakerInput.type = "hidden"
    addSpeakerInput.name = "add_speaker"
    addSpeakerInput.value = "1"
    form.appendChild(addSpeakerInput)

    // Submit the form
    form.requestSubmit()
  }

  /**
   * Remove a speaker from the dialogue block
   * Note: This submits the form to remove the speaker server-side
   */
  removeSpeaker(event) {
    event.preventDefault()

    const speakerIndex = event.target.dataset.speakerIndex
    if (this.speakerTargets.length <= 1) {
      alert("You must have at least one speaker.")
      return
    }

    if (!confirm("Are you sure you want to remove this speaker?")) {
      return
    }

    // Get the form and add a hidden flag to indicate removing a speaker
    const form = this.element.querySelector("form")
    if (!form) return

    const removeSpeakerInput = document.createElement("input")
    removeSpeakerInput.type = "hidden"
    removeSpeakerInput.name = "remove_speaker"
    removeSpeakerInput.value = speakerIndex
    form.appendChild(removeSpeakerInput)

    // Submit the form
    form.requestSubmit()
  }

  /**
   * Delete a dialogue line by clearing its text (will be filtered on save)
   */
  deleteLine(event) {
    event.preventDefault()

    const lineIndex = parseInt(event.target.closest("button").dataset.lineIndex, 10)
    const lineElement = this.lineTargets[lineIndex]

    if (!lineElement) return

    // Clear the text field - the line will be filtered out on save
    const textInput = lineElement.querySelector("input[name*='[text]']")
    if (textInput) {
      textInput.value = ""
    }

    // Visual feedback - hide the line
    lineElement.style.opacity = "0.3"
    lineElement.style.pointerEvents = "none"

    // Add a visual indicator that it will be deleted on save
    const indicator = document.createElement("span")
    indicator.className = "text-xs text-red-500 ml-2"
    indicator.textContent = "(will be deleted on save)"
    lineElement.querySelector("span")?.appendChild(indicator)
  }

  /**
   * Update speaker dropdown options in dialogue lines when speakers change
   */
  updateSpeakerOptions() {
    const speakerNames = this.speakerTargets.map((speaker, index) => {
      const nameInput = speaker.querySelector("input[name*='[name]']")
      return nameInput?.value || `Speaker ${index + 1}`
    })

    // Update all line speaker dropdowns
    const speakerSelects = [
      ...this.lineSpeakerTargets,
      ...this.newLineSpeakerTargets
    ]

    speakerSelects.forEach((select) => {
      const currentValue = select.value
      select.innerHTML = ""

      speakerNames.forEach((name, index) => {
        const option = document.createElement("option")
        option.value = index
        option.textContent = name
        if (index.toString() === currentValue) {
          option.selected = true
        }
        select.appendChild(option)
      })
    })
  }
}
